<template>
  <div>
    <base-header
      class="pb-9 pt-4"
      :type="$route.meta.headerClass || 'primary'"
    >
    </base-header>

    <div class="container-fluid mt--9">
      <!-- Stats row -->
      <div class="row mb-4">
        <div class="col-xl-3 col-md-6 mb-3">
          <div class="card shadow border-0">
            <div class="card-body p-3">
              <div class="row no-gutters align-items-center">
                <div class="col">
                  <div class="text-xs font-weight-bold text-uppercase mb-1 text-muted">Matches Today</div>
                  <div class="h4 mb-0">{{ matchesWithInput.length }}</div>
                </div>
                <div class="col-auto pl-2">
                  <div class="icon icon-shape bg-primary text-white rounded-circle shadow-sm">
                    <i class="ni ni-calendar-grid-58"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-md-6 mb-3">
          <div class="card shadow border-0">
            <div class="card-body p-3">
              <div class="row no-gutters align-items-center">
                <div class="col">
                  <div class="text-xs font-weight-bold text-uppercase mb-1 text-muted">Active Flows</div>
                  <div class="h4 mb-0" :class="activeCount > 0 ? 'text-success' : ''">{{ activeCount }}</div>
                </div>
                <div class="col-auto pl-2">
                  <div class="icon icon-shape bg-success text-white rounded-circle shadow-sm">
                    <i class="fas fa-broadcast-tower"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-md-6 mb-3">
          <div class="card shadow border-0">
            <div class="card-body p-3">
              <div class="row no-gutters align-items-center">
                <div class="col">
                  <div class="text-xs font-weight-bold text-uppercase mb-1 text-muted">Standby</div>
                  <div class="h4 mb-0">{{ standbyCount }}</div>
                </div>
                <div class="col-auto pl-2">
                  <div class="icon icon-shape bg-secondary text-white rounded-circle shadow-sm">
                    <i class="fas fa-pause-circle"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-md-6 mb-3">
          <div class="card shadow border-0">
            <div class="card-body p-3">
              <div class="row no-gutters align-items-center">
                <div class="col">
                  <div class="text-xs font-weight-bold text-uppercase mb-1 text-muted">Active Alarms</div>
                  <div class="h4 mb-0" :class="totalAlarmCount > 0 ? 'text-danger' : ''">{{ totalAlarmCount }}</div>
                </div>
                <div class="col-auto pl-2">
                  <div class="icon icon-shape text-white rounded-circle shadow-sm" :class="totalAlarmCount > 0 ? 'bg-danger' : 'bg-secondary'">
                    <i class="fas fa-bell"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!loading && brightcoveMismatchMatches.length > 0"
        class="row mb-3"
      >
        <div class="col">
          <div class="alert alert-warning mb-0 d-flex align-items-start">
            <i class="fas fa-exclamation-triangle mt-1 mr-3 flex-shrink-0"></i>
            <div>
              <strong>
                {{ brightcoveMismatchMatches.length }} match{{ brightcoveMismatchMatches.length === 1 ? '' : 'es' }}
                with input on but Brightcove off
              </strong>
              <div class="small mt-1">
                MediaConnect is active but the Brightcove live job is not running.
                Activate from the match Live tab so both start together.
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="row">
        <div class="col text-center py-5">
          <loading-spinner></loading-spinner>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else-if="matchesWithInput.length === 0" class="row">
        <div class="col">
          <div class="card shadow">
            <div class="card-body text-center py-5 text-muted">
              <i class="fas fa-satellite-dish fa-2x mb-3 d-block"></i>
              No matches with input flows assigned in the last 4 days.
            </div>
          </div>
        </div>
      </div>

      <!-- Main table -->
      <div v-else class="row">
        <div class="col">
          <div class="card shadow">
            <div class="card-header d-flex align-items-center justify-content-between flex-wrap">
              <span class="h3 mb-0">Input Flow Status</span>
              <div class="d-flex align-items-center">
                <span class="text-muted small mr-3">{{ dateLabel }}</span>
                <base-button
                  size="sm"
                  type="secondary"
                  :disabled="refreshingAll"
                  @click="refreshAll"
                >
                  <i class="fas fa-sync-alt mr-1" :class="{ 'fa-spin': refreshingAll }"></i>
                  Refresh all
                </base-button>
              </div>
            </div>
            <el-table
              class="table-card"
              :data="sortedMatches"
              row-key="id"
              header-row-class-name="thead-light"
              :row-class-name="rowClassName"
            >
              <el-table-column label="Match" min-width="200">
                <template v-slot="{ row }">
                  <div class="font-weight-bold">{{ row.team1Name }} vs {{ row.team2Name }}</div>
                  <div class="small text-muted">{{ row.competitionName || row.competition || '' }}</div>
                </template>
              </el-table-column>

              <el-table-column label="KO" width="120">
                <template v-slot="{ row }">
                  <span class="small">{{ formatKO(row.startDate) }}</span>
                </template>
              </el-table-column>

              <el-table-column label="Input" min-width="160">
                <template v-slot="{ row }">
                  <div v-if="inputForMatch(row)">
                    <span>{{ inputForMatch(row).name }}</span>
                    <span class="badge badge-light ml-1 text-uppercase" style="font-size:10px;">{{ typeShort(inputForMatch(row).type) }}</span>
                  </div>
                  <span v-else class="text-muted small font-italic">{{ row.livestreamInputId }}</span>
                </template>
              </el-table-column>

              <el-table-column label="Flow Status" width="130">
                <template v-slot="{ row }">
                  <span :class="['badge', 'badge-pill', flowStatusBadgeClass(inputFlowStatus(row))]" style="font-size:12px; padding:5px 10px;">
                    {{ inputFlowStatus(row) || 'UNKNOWN' }}
                  </span>
                </template>
              </el-table-column>

              <el-table-column label="Signal" width="110">
                <template v-slot="{ row }">
                  <span v-if="inputSourceHealth(row)" :class="['badge', 'badge-pill', healthBadgeClass(inputSourceHealth(row))]" style="font-size:12px; padding:5px 10px;">
                    {{ healthLabel(inputSourceHealth(row)) }}
                  </span>
                  <span v-else class="text-muted small">—</span>
                </template>
              </el-table-column>

              <el-table-column label="Takers" min-width="200">
                <template v-slot="{ row }">
                  <div v-if="matchTakerRows(row).length === 0" class="text-muted small">None</div>
                  <div v-for="tr in matchTakerRows(row)" :key="tr.takerId" class="taker-row d-flex align-items-center mb-1">
                    <span class="badge badge-light mr-1 text-uppercase flex-shrink-0" style="font-size:9px; min-width:22px;">
                      {{ typeShort(takerType(tr.takerId)) }}
                    </span>
                    <span class="small mr-1 flex-grow-1 text-truncate" style="max-width:110px;" :title="takerName(tr.takerId)">
                      {{ takerName(tr.takerId) }}
                    </span>
                    <span v-if="takerStatusForMatch(row, tr.takerId)" :class="['badge', 'badge-pill', takerStatusBadgeClass(takerStatusForMatch(row, tr.takerId))]" style="font-size:9px; padding:3px 6px; flex-shrink:0;">
                      {{ takerStatusLabel(takerStatusForMatch(row, tr.takerId)) }}
                    </span>
                  </div>
                </template>
              </el-table-column>

              <el-table-column label="Alarms" min-width="170">
                <template v-slot="{ row }">
                  <div v-if="alarmsForMatch(row).length === 0" class="text-muted small">—</div>
                  <div v-for="alarm in alarmsForMatch(row)" :key="alarm.type" class="d-flex align-items-center mb-1">
                    <span :class="['badge', 'badge-pill', 'mr-1', alarmSeverityClass(alarm.severity)]" style="font-size:10px; padding:3px 6px; flex-shrink:0;">
                      {{ alarm.severity === 'critical' ? 'CRIT' : 'WARN' }}
                    </span>
                    <span class="small font-weight-bold" :class="alarm.severity === 'critical' ? 'text-danger' : 'text-warning'">
                      {{ alarmTypeLabel(alarm.type) }}
                    </span>
                  </div>
                </template>
              </el-table-column>

              <el-table-column label="Updated" width="90">
                <template v-slot="{ row }">
                  <span class="small text-muted">{{ formatUpdated(inputForMatch(row)) }}</span>
                </template>
              </el-table-column>

              <el-table-column label="" width="120" align="right">
                <template v-slot="{ row }">
                  <base-button
                    size="sm"
                    type="secondary"
                    class="mr-1 px-2"
                    :disabled="isRefreshing(row)"
                    @click="refreshInput(row)"
                    title="Refresh flow status from AWS"
                  >
                    <i class="fas fa-sync-alt" :class="{ 'fa-spin': isRefreshing(row) }"></i>
                  </base-button>
                  <router-link
                    class="btn btn-sm btn-outline-primary mb-0"
                    :to="{ name: 'Match Assets', params: { id: row.id }, query: { tab: 'live' } }"
                  >
                    Live
                  </router-link>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Firebase } from '@/firebase.js'

const STATUS_ORDER = { ACTIVE: 0, STARTING: 1, STOPPING: 2, STANDBY: 3, IDLE: 3 }
const BRIGHTCOVE_LIVE_STATES = new Set([
  'on', 'processing', 'waiting', 'waiting_input', 'waiting_for_input', 'connected', 'active', 'starting',
])
// One-Day, T20, The Hundred — only shown when startDate is today
const SHORT_FORMATS = new Set(['2', '3', '4'])

export default {
  name: 'InputDashboard',
  data() {
    return {
      matches: [],
      loading: true,
      refreshingInputIds: {},
      refreshingAll: false,
      takerStatusByMatch: {},
    }
  },
  computed: {
    matchesWithInput() {
      return this.matches.filter((m) => m && m.livestreamInputId && this.shouldShowMatch(m))
    },
    sortedMatches() {
      return [...this.matchesWithInput].sort((a, b) => {
        const sa = STATUS_ORDER[this.inputFlowStatus(a)] ?? 99
        const sb = STATUS_ORDER[this.inputFlowStatus(b)] ?? 99
        if (sa !== sb) return sa - sb
        return (this.startMs(a.startDate) || 0) - (this.startMs(b.startDate) || 0)
      })
    },
    activeCount() {
      return this.matchesWithInput.filter((m) => this.inputFlowStatus(m) === 'ACTIVE').length
    },
    standbyCount() {
      return this.matchesWithInput.filter((m) => {
        const s = this.inputFlowStatus(m)
        return s === 'STANDBY' || s === 'IDLE'
      }).length
    },
    transitionCount() {
      return this.matchesWithInput.filter((m) => {
        const s = this.inputFlowStatus(m)
        return s === 'STARTING' || s === 'STOPPING'
      }).length
    },
    sessionsByMatchId() {
      const data = this.$store.state.livestreams.data || {}
      const map = {}
      Object.values(data).forEach((s) => {
        if (s && s.matchId && s.session_status === 'active') map[s.matchId] = s
      })
      return map
    },
    totalAlarmCount() {
      return Object.values(this.sessionsByMatchId).reduce((sum, s) => {
        const alarms = s.monitoring && s.monitoring.current && Array.isArray(s.monitoring.current.activeAlarms)
          ? s.monitoring.current.activeAlarms : []
        return sum + alarms.length
      }, 0)
    },
    brightcoveMismatchMatches() {
      return this.matchesWithInput.filter((m) => this.brightcoveMismatch(m))
    },
    brightcoveMismatchCount() {
      return this.brightcoveMismatchMatches.length
    },
    dateLabel() {
      const now = new Date()
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 3)
      const fmt = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
      return `${fmt(start)} – ${fmt(now)}`
    },
  },
  mounted() {
    this.$store.dispatch('inputs/openDBChannel').catch(console.error)
    this.$store.dispatch('takers/fetchAndAdd')
    this.loadRecentMatches()
  },
  beforeDestroy() {
    this.$store.dispatch('livestreams/closeDBChannel').catch(() => {})
    this.$store.dispatch('inputs/closeDBChannel').catch(() => {})
  },
  methods: {
    async loadRecentMatches() {
      this.loading = true
      try {
        const now = new Date()
        const startOfRange = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 3)
        const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
        const db = Firebase.app().firestore()
        const snap = await db.collection('matches')
          .where('startDate', '>=', startOfRange)
          .where('startDate', '<', endOfToday)
          .limit(200)
          .get()
        this.matches = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        const matchIds = this.matches
          .filter((m) => m.livestreamInputId && this.shouldShowMatch(m))
          .map((m) => m.id)
        if (matchIds.length > 0) {
          this.openSessionsChannel(matchIds)
        }
      } catch (e) {
        console.error('[InputDashboard] loadRecentMatches failed', e)
      } finally {
        this.loading = false
        this.refreshAll().catch(console.error)
      }
    },
    openSessionsChannel(matchIds) {
      // vuex-easy-firestore openDBChannel — live Firestore subscription.
      // Firestore 'in' supports up to 30 values; open one channel per batch.
      const batchSize = 30
      for (let i = 0; i < matchIds.length; i += batchSize) {
        const batch = matchIds.slice(i, i + batchSize)
        this.$store.dispatch('livestreams/openDBChannel', {
          clauses: {
            where: [
              ['matchId', 'in', batch],
              ['session_status', '==', 'active'],
            ],
          },
        }).catch(console.error)
      }
    },
    inputForMatch(match) {
      if (!match || !match.livestreamInputId) return null
      return this.$store.getters['inputs/byId'](match.livestreamInputId) || null
    },
    inputFlowStatus(match) {
      const inp = this.inputForMatch(match)
      if (!inp) return null
      return inp.flow_status ? String(inp.flow_status).toUpperCase() : null
    },
    inputSourceHealth(match) {
      const inp = this.inputForMatch(match)
      if (!inp) return null
      const status = this.inputFlowStatus(match)
      if (status !== 'ACTIVE') return null
      return inp.source_health_state ? String(inp.source_health_state).toUpperCase() : null
    },
    flowStatusBadgeClass(status) {
      switch (status) {
        case 'ACTIVE': return 'badge-success'
        case 'STARTING': return 'badge-primary'
        case 'STOPPING': return 'badge-warning'
        case 'STANDBY':
        case 'IDLE': return 'badge-secondary'
        default: return 'badge-light'
      }
    },
    healthBadgeClass(state) {
      switch (state) {
        case 'HEALTHY': return 'badge-success'
        case 'DEGRADED': return 'badge-warning'
        case 'OFFLINE': return 'badge-danger'
        default: return 'badge-secondary'
      }
    },
    healthLabel(state) {
      if (!state) return ''
      return state.charAt(0) + state.slice(1).toLowerCase()
    },
    rowClassName({ row }) {
      const alarms = this.alarmsForMatch(row)
      const hasCritical = alarms.some((a) => a.severity === 'critical')
      const hasWarning = alarms.some((a) => a.severity === 'warning')
      if (hasCritical) return 'dashboard-row--alarm-critical'
      if (this.brightcoveMismatch(row)) return 'dashboard-row--bc-mismatch'
      if (hasWarning) return 'dashboard-row--alarm-warning'
      const status = this.inputFlowStatus(row)
      if (status === 'ACTIVE') return 'dashboard-row--active'
      if (status === 'STARTING' || status === 'STOPPING') return 'dashboard-row--transition'
      return ''
    },
    typeShort(type) {
      if (type === 'mediaconnect') return 'MC'
      if (type === 'medialive') return 'ML'
      if (type === 'brightcove') return 'BC'
      return type || '?'
    },
    isStartDateToday(startDate) {
      const ms = this.startMs(startDate)
      if (!ms) return false
      const d = new Date(ms)
      const now = new Date()
      return d.getFullYear() === now.getFullYear()
        && d.getMonth() === now.getMonth()
        && d.getDate() === now.getDate()
    },
    shouldShowMatch(match) {
      if (!match) return false
      const format = match.cricketFormat != null ? String(match.cricketFormat) : ''
      if (SHORT_FORMATS.has(format) && !this.isStartDateToday(match.startDate)) {
        return false
      }
      return true
    },
    startMs(startDate) {
      if (!startDate) return null
      if (typeof startDate === 'number') return startDate
      if (startDate instanceof Date) return startDate.getTime()
      if (startDate && startDate.toMillis) return startDate.toMillis()
      if (startDate && startDate.toDate) return startDate.toDate().getTime()
      const p = Date.parse(startDate)
      return isNaN(p) ? null : p
    },
    formatKO(startDate) {
      const ms = this.startMs(startDate)
      if (!ms) return '—'
      const d = new Date(ms)
      const now = new Date()
      const isToday = d.getFullYear() === now.getFullYear()
        && d.getMonth() === now.getMonth()
        && d.getDate() === now.getDate()
      const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
      if (isToday) return time
      const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
      return `${date} ${time}`
    },
    formatUpdated(input) {
      if (!input || !input.flow_status_updated_at) return '—'
      const ms = typeof input.flow_status_updated_at === 'number'
        ? input.flow_status_updated_at
        : (input.flow_status_updated_at.toMillis ? input.flow_status_updated_at.toMillis() : null)
      if (!ms) return '—'
      const diff = Date.now() - ms
      if (diff < 60000) return 'Just now'
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
      return new Date(ms).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    },
    isRefreshing(match) {
      return !!this.refreshingInputIds[match.livestreamInputId]
    },
    async refreshInput(match) {
      const inputId = match && match.livestreamInputId
      if (!inputId || this.refreshingInputIds[inputId]) return
      this.$set(this.refreshingInputIds, inputId, true)
      try {
        const fn = Firebase.app().functions('europe-west2').httpsCallable('getInputFlowStatus')
        const result = await fn({ inputId, matchId: match.id })
        const statuses = result && result.data && result.data.brightcoveTakerStatuses
        if (Array.isArray(statuses)) {
          const byTaker = {}
          statuses.forEach((s) => { if (s && s.takerId) byTaker[s.takerId] = s.status })
          this.$set(this.takerStatusByMatch, match.id, byTaker)
        }
        // Alarms update automatically via the live openDBChannel subscription
      } catch (e) {
        console.error('[InputDashboard] refreshInput failed', e)
        this.$notify({ type: 'danger', message: e.message || 'Failed to refresh flow status', timeout: 4000 })
      } finally {
        this.$set(this.refreshingInputIds, inputId, false)
      }
    },
    async refreshAll() {
      if (this.refreshingAll) return
      this.refreshingAll = true
      try {
        const unique = {}
        this.matchesWithInput.forEach((m) => {
          if (m.livestreamInputId) unique[m.livestreamInputId] = m
        })
        await Promise.allSettled(
          Object.values(unique).map((m) => this.refreshInput(m))
        )
      } finally {
        this.refreshingAll = false
      }
    },
    matchTakerRows(match) {
      if (!match || !Array.isArray(match.livestreamTakerRoutes)) return []
      return match.livestreamTakerRoutes.filter((r) => r && r.takerId && r.enabled !== false)
    },
    takerForId(takerId) {
      return this.$store.getters['takers/byId'](takerId) || null
    },
    takerName(takerId) {
      const t = this.takerForId(takerId)
      return (t && (t.name || t.key)) || takerId
    },
    takerType(takerId) {
      const t = this.takerForId(takerId)
      return (t && t.type) || null
    },
    takerStatusForMatch(match, takerId) {
      const byTaker = this.takerStatusByMatch[match.id]
      if (!byTaker) return null
      return byTaker[takerId] || null
    },
    isFlowOn(match) {
      const status = this.inputFlowStatus(match)
      return status === 'ACTIVE' || status === 'STARTING'
    },
    brightcoveTakerRows(match) {
      return this.matchTakerRows(match).filter((r) => {
        const type = String(this.takerType(r.takerId) || '').toLowerCase()
        return type === 'brightcove'
      })
    },
    isBrightcoveLiveStatus(status) {
      if (!status) return false
      return BRIGHTCOVE_LIVE_STATES.has(String(status).toLowerCase())
    },
    brightcoveMismatch(match) {
      if (!this.isFlowOn(match)) return false
      const bcRows = this.brightcoveTakerRows(match)
      if (bcRows.length === 0) return false
      const byTaker = this.takerStatusByMatch[match.id]
      if (!byTaker) return false
      return bcRows.some((r) => !this.isBrightcoveLiveStatus(byTaker[r.takerId]))
    },
    takerStatusBadgeClass(status) {
      if (!status) return 'badge-secondary'
      const s = String(status).toLowerCase()
      if (BRIGHTCOVE_LIVE_STATES.has(s)) return 'badge-success'
      if (s === 'error') return 'badge-danger'
      if (s === 'missing_live_asset_id') return 'badge-warning'
      if (s === 'off' || s === 'standby') return 'badge-secondary'
      return 'badge-secondary'
    },
    takerStatusLabel(status) {
      if (!status) return ''
      return String(status).replace(/_/g, ' ')
    },
    alarmsForMatch(match) {
      const session = this.sessionsByMatchId[match.id]
      if (!session) return []
      return (session.monitoring && session.monitoring.current && Array.isArray(session.monitoring.current.activeAlarms))
        ? session.monitoring.current.activeAlarms
        : []
    },
    alarmSeverityClass(severity) {
      return severity === 'critical' ? 'badge-danger' : 'badge-warning'
    },
    alarmTypeLabel(type) {
      const labels = {
        NO_SIGNAL: 'No Signal',
        LOW_BITRATE: 'Low Bitrate',
        RESOLUTION_DRIFT: 'Resolution Drift',
        SCAN_TYPE_MISMATCH: 'Scan Mismatch',
        FLOW_STUCK_STARTING: 'Stuck Starting',
        UNEXPECTED_STOP: 'Unexpected Stop',
        BLACK_FRAMES: 'Black Frames',
        FROZEN_FRAMES: 'Frozen Frames',
        SILENT_AUDIO: 'Silent Audio',
        AUDIO_MISSING: 'Audio Missing',
        VIDEO_MISSING: 'Video Missing',
      }
      return labels[type] || type
    },
  },
}
</script>

<style scoped>
.dashboard-row--active td {
  background-color: rgba(45, 206, 137, 0.05) !important;
}
.dashboard-row--transition td {
  background-color: rgba(251, 99, 64, 0.05) !important;
}
.icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.taker-row:last-child {
  margin-bottom: 0 !important;
}
.dashboard-row--alarm-critical td {
  background-color: rgba(245, 54, 92, 0.06) !important;
}
.dashboard-row--alarm-warning td {
  background-color: rgba(251, 99, 64, 0.06) !important;
}
.dashboard-row--bc-mismatch td {
  background-color: rgba(255, 193, 7, 0.12) !important;
}
</style>
